import React, { useState } from "react";
import { Helmet } from "react-helmet-async";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Card } from "../components/ui/card";
import { Mail, Phone, MapPin, Clock } from "lucide-react";
import { apiService } from "../services/api";
import { toast } from "sonner";
import { countryCodes } from "../utils/countryCodes";

const Contact = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    message: "",
  });

  const [errors, setErrors] = useState({
    email: "",
    phone: "",
  });

  const [countryCode, setCountryCode] = useState("+1");
  const [detectedCountry, setDetectedCountry] = useState("US");
  const [geoData, setGeoData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  // Fetch geolocation on component mount
  React.useEffect(() => {
    const fetchGeoLocation = async () => {
      try {
        const { getGeoLocation, getCountryCodeFromGeo } =
          await import("../services/leadApi");
        const geoLocationData = await getGeoLocation();

        if (geoLocationData?.isSuccess) {
          setGeoData(geoLocationData);
          const countryInfo = getCountryCodeFromGeo(geoLocationData);
          setCountryCode(countryInfo.countryCode);
          setDetectedCountry(countryInfo.country);
        }
      } catch (error) {
        console.error("Failed to fetch geolocation:", error);
      }
    };

    fetchGeoLocation();
  }, []);

  // Email validation
  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  // Phone validation - exactly 10 digits
  const validatePhone = (phone) => {
    const digitsOnly = phone.replace(/[^\d]/g, "");
    return digitsOnly.length === 10;
  };

  const handleEmailBlur = () => {
    if (formData.email && !validateEmail(formData.email)) {
      setErrors((prev) => ({
        ...prev,
        email: "Please enter a valid email address (e.g., user@example.com)",
      }));
    } else {
      setErrors((prev) => ({ ...prev, email: "" }));
    }
  };

  const handlePhoneBlur = () => {
    if (formData.phone && !validatePhone(formData.phone)) {
      setErrors((prev) => ({ ...prev, phone: "Must be exactly 10 digits" }));
    } else {
      setErrors((prev) => ({ ...prev, phone: "" }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate before submission
    let hasErrors = false;
    const newErrors = { email: "", phone: "" };

    if (!validateEmail(formData.email)) {
      newErrors.email =
        "Please enter a valid email address (e.g., user@example.com)";
      hasErrors = true;
    }

    if (!validatePhone(formData.phone)) {
      newErrors.phone = "Must be exactly 10 digits";
      hasErrors = true;
    }

    if (hasErrors) {
      setErrors(newErrors);
      toast.error("Please fix the errors before submitting");
      return;
    }

    setIsLoading(true);

    try {
      // Import lead API dynamically
      const { submitLead } = await import("../services/leadApi");

      const leadData = {
        name: formData.name,
        email: formData.email,
        phone: formData.phone.replace(/\D/g, ""), // Extract only numbers
        countryCode: countryCode,
        country: detectedCountry,
        description: formData.message,
      };

      // Use pre-fetched geoData
      const result = await submitLead(leadData, geoData);

      if (result.success) {
        toast.success(result.message || "Message sent successfully!");
        // Redirect will be handled automatically by submitLead
      } else {
        toast.error(
          result.message || "Failed to send message. Please try again.",
        );
        setIsLoading(false);
      }
    } catch (error) {
      console.error("Submission error:", error);
      toast.error("An error occurred. Please try again later.");
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen pt-16">
      <Helmet>
        <title>
          Contact Us - Get Expert Trademark Registration Help | Trademark
          Essential
        </title>
        <meta
          name="description"
          content="Need help with trademark registration? Contact our expert team at +1 (877) 317-1184 / +1 (213) 973-2126. Fast response, professional guidance, free consultation."
        />
      </Helmet>
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-[#2B0A12] via-[#3D1C24] to-[#2B0A12] py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-5xl font-bold text-[#F1F1F1] mb-6">Contact Us</h1>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto">
            Have questions about protecting your intellectual property? Our team
            is here to help.
          </p>
        </div>
      </section>

      {/* Contact Information & Form */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            {/* Contact Info Cards */}
            <div className="space-y-6">
              <Card className="p-6 hover:shadow-xl transition-shadow">
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-[#FFB400] to-[#D72638] rounded-lg flex items-center justify-center flex-shrink-0">
                    <Phone className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-[#2B0A12] mb-2">
                      Phone
                    </h3>
                    <p className="text-gray-600">+1 (877) 317-1184</p>
                    <p className="text-gray-600">+1 (213) 973-2126</p>
                    <p className="text-sm text-gray-500 mt-1">
                      Mon-Fri, 9AM-6PM EST
                    </p>
                  </div>
                </div>
              </Card>

              <Card className="p-6 hover:shadow-xl transition-shadow">
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-[#FFB400] to-[#D72638] rounded-lg flex items-center justify-center flex-shrink-0">
                    <Mail className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-[#2B0A12] mb-2">
                      Email
                    </h3>
                    <p className="text-gray-600">info@trademarkessential.com</p>
                    <p className="text-sm text-gray-500 mt-1">
                      We'll respond within 24 hours
                    </p>
                  </div>
                </div>
              </Card>

              <Card className="p-6 hover:shadow-xl transition-shadow">
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-[#FFB400] to-[#D72638] rounded-lg flex items-center justify-center flex-shrink-0">
                    <MapPin className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-[#2B0A12] mb-2">
                      Office
                    </h3>
                    <p className="text-gray-600">
                      7060 Hollywood Blvd
                      <br />
                      Los Angeles, CA 90028
                    </p>
                  </div>
                </div>
              </Card>

              <Card className="p-6 hover:shadow-xl transition-shadow">
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-[#FFB400] to-[#D72638] rounded-lg flex items-center justify-center flex-shrink-0">
                    <Clock className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-[#2B0A12] mb-2">
                      Business Hours
                    </h3>
                    <p className="text-gray-600">
                      Monday - Friday: 9AM - 6PM
                      <br />
                      Saturday: 10AM - 4PM
                      <br />
                      Sunday: Closed
                    </p>
                  </div>
                </div>
              </Card>
            </div>

            {/* Contact Form */}
            <div className="lg:col-span-2">
              <Card className="p-8">
                <h2 className="text-3xl font-bold text-[#2B0A12] mb-6">
                  Send Us a Message
                </h2>
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Input
                        type="text"
                        placeholder="Full Name *"
                        value={formData.name}
                        onChange={(e) =>
                          setFormData({ ...formData, name: e.target.value })
                        }
                        required
                      />
                    </div>
                    <div>
                      <Input
                        type="email"
                        placeholder="Email Address *"
                        value={formData.email}
                        onChange={(e) =>
                          setFormData({ ...formData, email: e.target.value })
                        }
                        onBlur={handleEmailBlur}
                        className={errors.email ? "border-red-500" : ""}
                        required
                      />
                      {errors.email && (
                        <p className="text-red-500 text-sm mt-1">
                          {errors.email}
                        </p>
                      )}
                    </div>
                  </div>
                  <div>
                    <div className="flex space-x-2">
                      <select
                        value={countryCode}
                        onChange={(e) => setCountryCode(e.target.value)}
                        className="w-28 px-2 py-2 border border-gray-300 rounded-md bg-white text-sm font-medium text-gray-700 focus:outline-none focus:ring-2 focus:ring-[#FFB400] focus:border-[#FFB400] cursor-pointer"
                      >
                        {countryCodes.map((country, index) => (
                          <option key={index} value={country.code}>
                            {country.flag} {country.code}
                          </option>
                        ))}
                      </select>
                      <Input
                        type="tel"
                        placeholder="123-456-7890 *"
                        value={formData.phone}
                        onChange={(e) => {
                          const value = e.target.value.replace(
                            /[^\d\s\-]/g,
                            "",
                          );
                          const digitsOnly = value.replace(/[^\d]/g, "");
                          if (digitsOnly.length <= 10) {
                            setFormData({ ...formData, phone: value });
                          }
                        }}
                        onBlur={handlePhoneBlur}
                        className={`flex-1 ${errors.phone ? "border-red-500" : ""}`}
                        required
                        maxLength={12}
                      />
                    </div>
                    {errors.phone && (
                      <p className="text-red-500 text-sm mt-1">
                        ⚠️ {errors.phone}
                      </p>
                    )}
                    <p className="text-xs text-gray-500 mt-1">
                      Format: 123-456-7890 (exactly 10 digits)
                    </p>
                  </div>
                  <Textarea
                    placeholder="Your Message *"
                    value={formData.message}
                    onChange={(e) =>
                      setFormData({ ...formData, message: e.target.value })
                    }
                    required
                    rows={6}
                  />
                  <Button
                    type="submit"
                    disabled={isLoading}
                    className="w-full bg-[#FFB400] hover:bg-[#e5a200] text-[#2B0A12] font-bold text-lg py-6"
                  >
                    {isLoading ? "Sending..." : "Send Message"}
                  </Button>
                </form>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Map Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-2xl overflow-hidden shadow-xl">
            <iframe
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3304.9567890123456!2d-118.3437!3d34.1016!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x80c2bf3ec9b4d7e5%3A0x7e9f8d6c5b4a3e2f!2s7060%20Hollywood%20Blvd%2C%20Los%20Angeles%2C%20CA%2090028!5e0!3m2!1sen!2sus!4v1234567890123!5m2!1sen!2sus"
              width="100%"
              height="450"
              style={{ border: 0 }}
              allowFullScreen=""
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title="Trademark Essential Office Location"
            ></iframe>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
